import { lazy, Suspense, useEffect, useRef, useState, type ReactNode } from "react";
import { ArrowUpRight, Download } from "lucide-react";
import { useLocation } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { trackEvent } from "@/lib/analytics";
import { appDownloadQrUrl, appStoreUrl, detectAppPlatform, type AppPlatform } from "@/lib/app-download";
import { cn } from "@/lib/utils";
import "@/styles/app-download.css";

const QRCode = lazy(() => import("qrcode.react").then((module) => ({ default: module.QRCodeSVG })));

/** Store links remain usable while the optional desktop QR loads. */
export const AppDownloadOptions = ({ source, children }: { source: string; children?: ReactNode }) => {
  const { language, t } = useLanguage();
  const { pathname } = useLocation();
  const qrRef = useRef<HTMLDivElement>(null);
  const [showQr, setShowQr] = useState(false);

  useEffect(() => {
    const media = window.matchMedia("(min-width: 761px)");
    let observer: IntersectionObserver | undefined;
    const loadWhenVisible = () => {
      observer?.disconnect();
      if (!media.matches || !qrRef.current) return;
      if (!("IntersectionObserver" in window)) {
        setShowQr(true);
        return;
      }
      observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          setShowQr(true);
          observer?.disconnect();
        }
      }, { rootMargin: "200px" });
      observer.observe(qrRef.current);
    };
    loadWhenVisible();
    media.addEventListener("change", loadWhenVisible);
    return () => {
      observer?.disconnect();
      media.removeEventListener("change", loadWhenVisible);
    };
  }, []);

  return (
    <div className="field-app-download-options">
      <div className="field-app-download-stores">
        {(["ios", "android"] as const).map((store) => (
          <a
            key={store}
            href={appStoreUrl(store)}
            target="_blank"
            rel="noopener noreferrer"
            className={cn("field-button", store === "android" && "field-button-secondary")}
            onClick={() => trackEvent("download_app_click", {
              store, source, placement: source, pathname, language,
            })}
          >
            {t(store === "ios" ? "field.download.apple" : "field.download.google")}
            <ArrowUpRight size={18} aria-hidden="true" />
          </a>
        ))}
        {children}
      </div>
      <div className="field-app-download-qr" ref={qrRef}>
        <div className="field-app-download-qr-code">
          {showQr && (
            <Suspense fallback={<span className="field-app-qr-placeholder" aria-hidden="true" />}>
              <QRCode
                value={appDownloadQrUrl(language, source)}
                size={144}
                marginSize={4}
                level="M"
                title={t("conversion.download.qrTitle")}
              />
            </Suspense>
          )}
        </div>
        <p>{t("conversion.download.qrHint")}</p>
      </div>
    </div>
  );
};

type AppDownloadActionProps = {
  source: string;
  className?: string;
  children?: ReactNode;
  compact?: boolean;
};

export const AppDownloadAction = ({ source, className, children, compact = false }: AppDownloadActionProps) => {
  const { language, t } = useLanguage();
  const { pathname } = useLocation();
  const [platform, setPlatform] = useState<AppPlatform>("desktop");
  const [open, setOpen] = useState(false);
  useEffect(() => setPlatform(detectAppPlatform()), []);

  const content = (
    <>
      {children ?? t(compact ? "conversion.download.compact" : "conversion.download.cta")}
      <Download size={compact ? 15 : 18} aria-hidden="true" />
    </>
  );
  const buttonClass = cn("field-button field-app-download-action", className);

  if (platform !== "desktop") {
    return (
      <a
        href={appStoreUrl(platform)}
        className={buttonClass}
        onClick={() => trackEvent("download_app_click", {
          store: platform, source, placement: source, pathname, language,
        })}
      >
        {content}
      </a>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => {
      setOpen(nextOpen);
      if (nextOpen) trackEvent("app_download_open", { source, placement: source, pathname, language });
    }}>
      <DialogTrigger asChild>
        <button type="button" className={buttonClass}>{content}</button>
      </DialogTrigger>
      {open && (
        <DialogContent
          closeLabel={t("conversion.download.close")}
          className="field-app-download-dialog"
          overlayClassName="field-app-download-overlay"
        >
          <DialogHeader>
            <DialogTitle>{t("conversion.download.dialogTitle")}</DialogTitle>
            <DialogDescription className="field-app-download-description">{t("conversion.download.dialogBody")}</DialogDescription>
          </DialogHeader>
          <AppDownloadOptions source={source} />
          <p className="field-app-download-terms">{t("field.download.note")}</p>
        </DialogContent>
      )}
    </Dialog>
  );
};
